import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { apiCall } from '@/utils/apiAxios';
import dayjs from 'dayjs';
import localeData from 'dayjs/plugin/localeData';
import es from 'dayjs/locale/es';
import { Header } from '@/types/table/table';
import { getCompanyName } from '@/config/configHelpers';
dayjs.extend(localeData);
dayjs.locale(es);

const STATUS_ORDER = ['2', '1'];

const sortStatusesActivity = <T extends { id: string }>(list: T[]): T[] => {
    const orderMap = STATUS_ORDER.reduce((acc, statusId, index) => {
        acc.set(statusId, index);
        return acc;
    }, new Map<string, number>());

    return [...list].sort((a, b) => {
        const orderA = orderMap.get(a.id) ?? Number.MAX_SAFE_INTEGER;
        const orderB = orderMap.get(b.id) ?? Number.MAX_SAFE_INTEGER;
        return orderA - orderB;
    });
};

export interface MovementType {
    id: string;
    text: string;
    value: string;
    label: string;
    short_title: string;
    required: boolean;
    kind: string;
    parent_id: string;
    parent_title?: string;
    parent_short_title?: string;
}

export interface Branch {
    id: string;
    name: string;
    text: string;
    label: string;
    title: string;
    value: string;
}

export const useFiltersStore = defineStore('filters', () => {
    const statusesActivity = ref(
        sortStatusesActivity([
            {
                id: '2',
                text: 'Abierto'
            },
            {
                id: '1',
                text: 'Cerrado'
            }
        ])
    );

    const subStatusesActivity = ref([
        {
            value: '1',
            label: 'Entregado',
            parent_id: ['1', '2']
        },
        {
            value: '2',
            label: 'Información incompleta',
            parent_id: ['2']
        },
        {
            value: '3',
            label: 'Información completa',
            parent_id: ['2']
        },
        {
            value: '4',
            label: 'Emitido',
            parent_id: ['1']
        },
        {
            value: '5',
            label: 'Recotizado',
            parent_id: ['1']
        },
        {
            value: '6',
            label: 'Cancelado',
            parent_id: ['1']
        }
    ]);

    const companyName = getCompanyName();

    const statusesPendingStatus = ref([
        {
            id: '1',
            text: 'Agente'
        },
        {
            id: '2',
            text: companyName
        }
    ]);
    const movementsActivity = ref<MovementType[]>([]);
    const accountTypesActivity = ref([]);

    const branches = ref<Branch[]>([]);

    // drawer

    const drawerDraggable = ref(false);
    const tableStore = ref<null | any>(null);
    const drawerStore = ref({
        headers: [] as Header[]
    });
    const tableName = ref('');
    const storageKey = ref('');

    // 2 => Cartera Total, 1 => Cartera (Brand), -1 => Cartera No (Brand)
    const WALLET_INITIAL_STATE = 1;

    const selectedWallet = ref<number>(WALLET_INITIAL_STATE);

    function getWalletTitle(value: number) {
        if (value == 2) {
            return 'Cartera Total';
        } else if (value == 1) {
            return 'Cartera ' + companyName;
        } else if (value == -1) {
            return 'Cartera No ' + companyName;
        }
    }

    const selectedWalletTitle = computed(() => getWalletTitle(selectedWallet.value));

    const setWallet = (wallet: number) => {
        selectedWallet.value = wallet;
    };

    const getFiltersActivity = async () => {
        const storedStatusesActivity = localStorage.getItem('statusesActivity');
        const storedMovementsActivity = localStorage.getItem('movementsActivity');
        const storedAccountTypesActivity = localStorage.getItem('accountTypesActivity');

        if (storedStatusesActivity || storedAccountTypesActivity) {
            statusesActivity.value = sortStatusesActivity(JSON.parse(storedStatusesActivity ? storedStatusesActivity : '[]'));
            accountTypesActivity.value = JSON.parse(storedAccountTypesActivity ? storedAccountTypesActivity : '[]');
        } else {
            getFiltersActivityGet();
        }

        if (storedMovementsActivity) {
            movementsActivity.value = JSON.parse(storedMovementsActivity ? storedMovementsActivity : '[]');
        } else {
            getAllMovementTypes();
        }
    };

    async function getFiltersActivityGet() {
        const data = {};

        const { request } = await apiCall({
            method: 'get',
            url: `/executive/api/activities_logs/get_array_constant`,
            data: {},
            params: data
        });

        const response = await request;
        if (response.status == 1) {
            statusesActivity.value = sortStatusesActivity(response.status_array);
            accountTypesActivity.value = response.account_type;

            localStorage.setItem('statusesActivity', JSON.stringify(statusesActivity.value));
            localStorage.setItem('accountTypesActivity', JSON.stringify(accountTypesActivity.value));
        } else {
            console.error('error al obtener los datos (getFiltersActivity)');
        }
    }

    async function getAllMovementTypes() {
        const { request } = await apiCall({
            method: 'get',
            url: `/executive/api/activities_logs/get_movements`,
            params: {
                version: 2,
                sub_category_id: -1,
                kind: -1
            }
        });

        const response = await request;

        if (response.status == 1) {
            movementsActivity.value = response.response;
            localStorage.setItem('movementsActivity', JSON.stringify(movementsActivity.value));
        } else {
            console.error('error al obtener los datos (getAllMovementTypes)');
        }
    }

    async function getAllBranches() {
        const storedBranches = localStorage.getItem('branches');

        if (storedBranches) {
            branches.value = JSON.parse(storedBranches || '[]');

            return;
        }

        const { request } = await apiCall({
            method: 'get',
            url: `/executive/api/commons/get_general_branches`
        });

        const response = await request;

        if (response.status == 1) {
            branches.value = response.response;
            localStorage.setItem('branches', JSON.stringify(branches.value));
        } else {
            console.error('error al obtener los datos (getAllBranches)');
        }
    }

    const initialFilters = () => {
        getFiltersActivity();
    };

    const getTextMovementType = (movement_type: string | null) => {
        let filteredKind = movementsActivity.value.filter((e) => e.id == movement_type);

        if (filteredKind.length > 0) {
            return filteredKind[0].text;
        } else {
            return 'Sin datos';
        }
    };

    const isMovementTypeCotizacionOrRecotizacion = (movementtypeId: string) => {
        if (!movementtypeId) return false;
        const movementText = getTextMovementType(movementtypeId);

        if (movementText.toLowerCase() == 'cotización' || movementText.toLowerCase() == 'recotización') {
            return true;
        }

        return false;
    };

    const getCatalogsAuto = async (year: string = new Date().getFullYear().toString(), category_id: string) => {
        const { request } = apiCall({
            method: 'get',
            url: `/multiquote/api/catalogs/autos_clean/${category_id}/${year}`
        });

        const response = await request;

        return response;
    };

    const getParentMovementByShortTitle = (shortTitle: string): MovementType | null => {
        if (!shortTitle || typeof shortTitle !== 'string') return null;

        const movement = movementsActivity.value.find(
            (movement) => movement.short_title === shortTitle && (!movement.parent_id || movement.parent_id == '0')
        );

        if (!movement) return null;

        return movement;
    };

    const getChildMovementByShortTitle = (shortTitle: string, parentTitle: string = ''): MovementType | null => {
        if (!shortTitle || typeof shortTitle !== 'string') return null;

        let movement;

        if (parentTitle != '') {
            movement = movementsActivity.value.find(
                (movement) =>
                    movement.short_title === shortTitle &&
                    movement.parent_title == parentTitle &&
                    movement.parent_id &&
                    movement.parent_id != '0'
            );
        } else {
            movement = movementsActivity.value.find(
                (movement) => movement.short_title === shortTitle && movement.parent_id && movement.parent_id != '0'
            );
        }

        if (!movement) return null;

        return movement;
    };

    const getChildMovementByParentShortTitleAndChildShortTitle = (
        parentShortTitle: string,
        childShortTitle: string
    ): MovementType | null => {
        if (!childShortTitle || !parentShortTitle || typeof parentShortTitle !== 'string' || typeof childShortTitle !== 'string')
            return null;

        const movement = movementsActivity.value.find(
            (movement) =>
                movement.parent_short_title === parentShortTitle &&
                movement.short_title === childShortTitle &&
                movement.parent_id &&
                movement.parent_id != '0'
        );

        if (!movement) return null;

        return movement;
    };

    const isEmissionMovement = (movementId: string) => {
        if (!movementId) return false;

        const movement = movementsActivity.value.find((mov) => mov.id == movementId);

        if (!movement) return false;

        return movement.short_title == 'emision';
    };

    // Filtro de rango de meses
    function capitalizeFirstLetter(string: string) {
        return string.charAt(0).toUpperCase() + string.slice(1);
    }

    function generateMonthsFilterOptions() {
        const options = [];
        const QTY_OF_MONTHS_IN_FILTER = 4;

        // opcion -3 meses
        const currentMonthOther = dayjs().startOf('month');
        const startDateOther = currentMonthOther.subtract(3, 'month').startOf('month').format('YYYY-MM-DD');
        const endDateOther = currentMonthOther.subtract(1, 'month').endOf('month').format('YYYY-MM-DD');

        // Los nombres del label, deben ser iguales que los del API
        options.push({
            label: '-3 Meses',
            value: {
                start_date: startDateOther,
                end_date: endDateOther
            }
        });

        // Otras opciones
        for (let i = 0; i < QTY_OF_MONTHS_IN_FILTER; i++) {
            const currentMonth = dayjs().add(i, 'month');

            options.push({
                label: capitalizeFirstLetter(currentMonth.format('MMMM')),
                value: {
                    start_date: currentMonth.startOf('month').format('YYYY-MM-DD'),
                    end_date: currentMonth.endOf('month').format('YYYY-MM-DD')
                }
            });
        }

        return options;
    }

    const monthsFilterOptions = ref(generateMonthsFilterOptions());

    const monthsFilter = ref([{ ...monthsFilterOptions.value[1].value }]);

    const allMonthsFilter = computed(() => {
        return monthsFilterOptions.value.map((option) => ({ ...option.value }));
    });

    const FILTERS_STORAGE_PREFIX = 'brokerin_saved_filters_';
    const getFiltersStorageKey = (table: string) => `${FILTERS_STORAGE_PREFIX}${table}`;

    const canUseLocalStorage = () => typeof window !== 'undefined' && !!window.localStorage;

    const getCachedFilters = (table: string) => {
        if (!canUseLocalStorage()) return null;
        const storageKey = getFiltersStorageKey(table);
        try {
            const storedFilters = localStorage.getItem(storageKey);
            return storedFilters ? JSON.parse(storedFilters) : null;
        } catch (error) {
            console.warn(`Error al leer localStorage para ${table}`, error);
            return null;
        }
    };

    const cacheFilters = (table: string, filters: any) => {
        if (!canUseLocalStorage()) return;
        const storageKey = getFiltersStorageKey(table);
        try {
            localStorage.setItem(storageKey, JSON.stringify(filters));
        } catch (error) {
            console.warn(`Error al almacenar filtros (cacheFilters) para ${table}`, error);
        }
    };

    const getDataFilter = async (table: string, force = false) => {
        if (!force) {
            const cachedFilters = getCachedFilters(table);
            if (cachedFilters) {
                return cachedFilters;
            }
        }

        const params = {
            table_name: table,
            platform: 'brokerin'
        };

        const { request } = apiCall({
            method: 'get',
            url: `/user/api/config_users/get_user_saved_filter`,
            params: params
        });

        try {
            const response = await request;

            if (response && response.status === 1) {
                const filters = response.response;
                cacheFilters(table, filters);
                return filters;
            }

            console.error(`Error al obtener los filtros (getDataFilter) para ${table}`);
            return null;
        } catch (error) {
            console.error(`Error al obtener los filtros (getDataFilter) para ${table}`, error);
            return null;
        }
    };

    // Guardar filtros API
    const saveFilters = async (filters: any, table: string) => {
        const data_post = {
            table_name: table,
            platform: 'brokerin',
            filter_save: JSON.stringify(filters)
        };

        const { request } = apiCall({
            method: 'post',
            url: `/user/api/config_users/upload_user_filter`,
            data: data_post
        });

        const response = await request;

        if (response && response.status === 1) {
            cacheFilters(table, filters);
            return true;
        } else {
            console.error(`Error al guardar los filtros (saveFilters) para ${table}`);
            return false;
        }
    };

    return {
        selectedWallet,
        statusesActivity,
        subStatusesActivity,
        movementsActivity,
        accountTypesActivity,
        monthsFilterOptions,
        monthsFilter,
        allMonthsFilter,
        selectedWalletTitle,
        drawerDraggable,
        drawerStore,
        tableName,
        storageKey,
        tableStore,
        statusesPendingStatus,
        WALLET_INITIAL_STATE,
        getTextMovementType,
        setWallet,
        getFiltersActivity,
        initialFilters,
        getCatalogsAuto,
        getWalletTitle,
        isMovementTypeCotizacionOrRecotizacion,
        getParentMovementByShortTitle,
        getChildMovementByShortTitle,
        getChildMovementByParentShortTitleAndChildShortTitle,
        getDataFilter,
        saveFilters,
        isEmissionMovement,
        cacheFilters,
        branches,
        getAllBranches
    };
});
